import React from 'react';
import type { PropertyAnalysisResult } from '../../types/calculations';
import { CheckCircle, TrendingUp, Loader2, Info } from 'lucide-react';
import { SubmarketComparisonTable } from './SubmarketComparisonTable';
import { useSubmarketComparison } from '../../hooks/useSubmarketComparison';
import { ZandiHistoricalChart, ZandiHistoricalChartSkeleton } from './ZandiHistoricalChart';
import { RentGapVisualization } from './RentGapVisualization';
import { SectionTitle, GoldBadge } from '../Luxury';
import { ZandiDealImpact } from './ZandiDealImpact';
import { ZandiYardiCrossValidation } from './ZandiYardiCrossValidation';
import { useYardiComparison } from '../../hooks/useYardiComparison';
import type { FullComparisonResponse } from '../../types/yardi';
import { InvestmentIntelligenceCard } from './InvestmentIntelligenceCard';
import { DemographicInsightsCard } from './DemographicInsightsCard';
import { RentGrowthCard } from './RentGrowthCard';
import { useActiveMarket } from '../../store/useMarketStore';
import { useSubmarketEgiHistory } from '../../hooks/useRentGrowth';
import { CensusDemographicPanel } from './CensusDemographicPanel';

interface Tab2Props {
    data: PropertyAnalysisResult;
}

// Formatting helpers
const formatScore = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return '--';
    return score.toFixed(1);
};

const getScoreInterpretation = (score: number): { label: string; badgeVariant: 'gold' | 'ssot' | 'off-market'; description: string } => {
    if (score >= 80) return { label: 'Excellent', badgeVariant: 'ssot', description: 'Top-quartile investment potential' };
    if (score >= 65) return { label: 'Good', badgeVariant: 'gold', description: 'Above-average appreciation expected' };
    if (score >= 50) return { label: 'Average', badgeVariant: 'off-market', description: 'Market-rate returns expected' };
    return { label: 'Below Average', badgeVariant: 'off-market', description: 'Higher risk, lower expected returns' };
};

const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '--';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '--';
    return `${value.toFixed(1)}%`;
};

// Helper functions for Yardi Market Context
const getForecastAssessment = (yardiData: FullComparisonResponse | undefined): string => {
    if (!yardiData?.market_context.forecast_2026) return 'Unknown';
    const current = yardiData.rent_growth_comparison.yardi_value;
    const forecast = yardiData.market_context.forecast_2026;

    if (forecast > current + 0.005) return 'Accelerating';
    if (forecast < current - 0.005) return 'Decelerating';
    return 'Stable';
};

const getSupplyAssessment = (completions: number | undefined): string => {
    if (completions === undefined || completions === null) return 'Unknown';
    if (completions > 0.03) return 'High';
    if (completions > 0.015) return 'Moderate';
    return 'Low';
};

const formatPercentFromDecimal = (value: number | undefined): string => {
    if (value === undefined || value === null) return '--';
    return `${(value * 100).toFixed(1)}%`;
};

/**
 * Mirrors backend momentum phase classification logic.
 * Compares submarket CAGR to metro CAGR and checks YoY acceleration.
 */
const getMomentumPhase = (
    cagr: number | undefined | null,
    yoy: number | undefined | null,
    metroCagr: number | undefined | null
): string => {
    if (cagr == null || yoy == null || metroCagr == null) return 'Unknown';
    const belowMetro = cagr < metroCagr;
    const accelerating = yoy > cagr;
    if (belowMetro && accelerating) return 'Entry Point (Best Time to Buy)';
    if (!belowMetro && accelerating) return 'Accelerating';
    if (!belowMetro && !accelerating) return 'Mature';
    return 'Declining';
};

/**
 * Generate synthetic 16-year EGI history from current EGI and CAGR.
 * Works backward from current year to estimate historical values.
 * Used as fallback for non-Philly markets that lack real EGI history data.
 *
 * Note: CAGR may arrive as decimal (0.077) or percentage (7.7) depending on source.
 */
function generateSyntheticHistory(currentEgi: number, cagr5yr: number): Array<{ year: number; egi: number }> {
    const currentYear = new Date().getFullYear();
    const history = [];

    // Normalize: if > 1, assume percentage and divide by 100; if <= 1, assume decimal
    const cagrDecimal = cagr5yr > 1 ? cagr5yr / 100 : cagr5yr;

    // Work backward from current year using CAGR (16 years of data)
    // CAGR = (End/Start)^(1/years) - 1
    // So Start = End / (1 + CAGR)^years
    for (let i = 15; i >= 0; i--) {
        const year = currentYear - i;
        const yearsFromNow = i;
        const egi = Math.round(currentEgi / Math.pow(1 + cagrDecimal, yearsFromNow));
        history.push({ year, egi });
    }

    return history;
}

// Helper: detect if a score is at its neutral default (50.0) due to missing raw data
const isScoreDefaulted = (
    score: number | null | undefined,
    rawDataAvailable: boolean
): boolean => {
    return score === 50.0 && !rawDataAvailable;
};

// Luxury Score Row Component
interface ScoreRowProps {
    weight: string;
    label: string;
    correlation: string;
    correlationPositive: boolean;
    description: string;
    score: number | null | undefined;
    colorClass: string;
    isActive: boolean;
    defaulted?: boolean;
}

const ScoreRow: React.FC<ScoreRowProps> = ({
    weight,
    label,
    correlation,
    correlationPositive,
    description,
    score,
    colorClass,
    isActive,
    defaulted = false,
}) => {
    const effectiveActive = isActive && !defaulted;
    return (
        <div className={`flex items-center gap-3 p-3 rounded border transition-all ${
            effectiveActive
                ? `bg-${colorClass}/10 border-${colorClass}/30`
                : 'bg-graphite/50 border-slate-dark'
        }`}>
            <div className="w-14 text-center">
                <span className={`text-lg font-bold font-mono ${effectiveActive ? `text-${colorClass}` : 'text-stone-700'}`}>
                    {weight}
                </span>
            </div>
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <span className="font-semibold text-ivory">{label}</span>
                    <span className={`text-sm font-mono ${correlationPositive ? 'text-emerald' : 'text-amber-400'}`}>
                        {correlation}
                    </span>
                </div>
                <p className="text-sm text-stone-700">{description}</p>
            </div>
            <div className={`w-14 text-right font-mono text-lg font-bold ${effectiveActive ? 'text-ivory' : 'text-stone-700'}`}>
                {score != null ? (defaulted ? '50*' : formatScore(score)) : '--'}
            </div>
        </div>
    );
};

// AssessmentBadge Component for SSOT vs Yardi comparison
interface AssessmentBadgeProps {
    assessment: 'CONSERVATIVE' | 'ALIGNED' | 'AGGRESSIVE';
}

const AssessmentBadge: React.FC<AssessmentBadgeProps> = ({ assessment }) => {
    const styles = {
        CONSERVATIVE: 'bg-emerald-500/20 text-emerald-400',
        ALIGNED: 'bg-amber-500/20 text-amber-400',
        AGGRESSIVE: 'bg-red-500/20 text-red-400',
    };

    return (
        <span className={`inline-block px-2 py-1 text-sm font-semibold uppercase tracking-wider rounded ${styles[assessment]}`}>
            {assessment}
        </span>
    );
};

export const Tab2Scoring: React.FC<Tab2Props> = ({ data }) => {
    const market = useActiveMarket();
    const scoring = data.scoring;
    const zandi = data.zandi_scoring;

    // Fetch submarket comparison data
    const submarketName = zandi?.name || '';
    const { data: submarketComparison, isLoading: isLoadingComparison } = useSubmarketComparison(
        submarketName,
        zandi
    );

    // Fetch Yardi market context
    const { data: yardiData, isLoading: yardiLoading } = useYardiComparison({
        metro: market.display_name,
        tier: ((data as any)?.ssot_params?.tier || 'Tier2') as 'Tier1' | 'Tier2' | 'Tier3',
        enabled: !!data,
    });

    // Fetch historical EGI time-series from API
    const egiHistory = useSubmarketEgiHistory(submarketName);

    // Detect non-Philadelphia market (Zandi scoring requires Moody's CRE data, Philly only)
    const isNonPhillyMarket = market.market_id !== 'philadelphia';

    // Non-Philly market: show market-aware info card + non-Zandi components only
    if (isNonPhillyMarket && !zandi) {
        return (
            <div className="space-y-6 bg-obsidian min-h-full">
                {/* No-Zandi Info Card */}
                <div className="p-6 bg-charcoal rounded-lg border border-amber-500/30">
                    <div className="flex items-start gap-4">
                        <Info className="w-6 h-6 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="text-lg font-serif italic text-gold mb-2">
                                Zandi Submarket Scoring
                            </h3>
                            <p className="text-sm text-stone-700 leading-relaxed">
                                Zandi submarket scoring is available for Philadelphia only. Scoring requires
                                Moody's CRE data (EGI, vacancy, value index, MOBI) which currently covers
                                the Philadelphia metro area's 29 submarkets.
                            </p>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="p-3 bg-graphite rounded border border-slate-dark">
                                    <p className="text-sm text-stone-700 uppercase tracking-wider">Coverage</p>
                                    <p className="text-sm font-semibold text-amber-400 mt-1">Philadelphia Only</p>
                                </div>
                                <div className="p-3 bg-graphite rounded border border-slate-dark">
                                    <p className="text-sm text-stone-700 uppercase tracking-wider">Data Source</p>
                                    <p className="text-sm font-semibold text-purple-300 mt-1">Moody's / Zandi CRE</p>
                                </div>
                                <div className="p-3 bg-graphite rounded border border-slate-dark">
                                    <p className="text-sm text-stone-700 uppercase tracking-wider">This Market</p>
                                    <p className="text-sm font-semibold text-stone-700 mt-1">{market.display_name}, {market.state}</p>
                                </div>
                            </div>
                            <p className="mt-3 text-sm text-stone-700 italic">
                                Underwriting metrics (Cap Rate, IRR, CoC) and demographic data are still available below.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Property Score card if scoring data exists */}
                {scoring && (
                    <div className={`bg-charcoal border-l-4 p-5 rounded-r-lg ${scoring.grade === 'A' ? 'border-emerald' : 'border-gold'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-serif italic text-gold">Property Score</h3>
                                <p className="mt-1 text-sm text-stone-700">Deal Quality</p>
                            </div>
                            <div className="text-right">
                                <p className={`text-4xl font-bold font-mono ${scoring.grade === 'A' ? 'text-emerald' : 'text-gold'}`}>
                                    {scoring.total_score}
                                </p>
                                <span className={`inline-block mt-1 px-2 py-0.5 text-sm font-semibold uppercase tracking-wider rounded ${
                                    scoring.grade === 'A' ? 'bg-emerald/20 text-emerald' : 'bg-gold/20 text-gold'
                                }`}>
                                    Grade {scoring.grade}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Market Assumptions — rates driving the underwriting for this market */}
                {(() => {
                    const tier = (data.rent_capex_transparency?.tier ?? (data as any)?.ssot_params?.tier ?? 'Tier2') as string;
                    const opexRate = data.rent_capex_transparency?.opex_rate;
                    const vacancyRate = data.inputs?.vacancy_rate;        // percentage (e.g. 7.0 = 7%)
                    const rentGrowth = (data as any)?.inputs?.rent_growth; // decimal (e.g. 0.03 = 3%)
                    const rentSource = data.rent_capex_transparency?.current_rent_source;

                    return (
                        <div className="p-5 bg-charcoal rounded-lg border border-slate-dark">
                            <div className="flex items-center gap-2 mb-4">
                                <CheckCircle className="w-4 h-4 text-emerald" />
                                <h4 className="text-sm font-semibold text-ivory uppercase tracking-wider">
                                    {market.display_name} Market Assumptions
                                </h4>
                                <span className="ml-auto text-sm text-stone-700 italic">
                                    Driving this underwriting
                                </span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <div className="p-3 bg-graphite rounded border border-slate-dark">
                                    <p className="text-sm text-stone-700 uppercase tracking-wider">Tier</p>
                                    <p className="text-sm font-semibold text-gold mt-1">{tier}</p>
                                </div>
                                {opexRate != null && (
                                    <div className="p-3 bg-graphite rounded border border-slate-dark">
                                        <p className="text-sm text-stone-700 uppercase tracking-wider">OpEx Ratio</p>
                                        <p className="text-sm font-semibold text-ivory mt-1">{(opexRate * 100).toFixed(0)}%</p>
                                    </div>
                                )}
                                {vacancyRate != null && (
                                    <div className="p-3 bg-graphite rounded border border-slate-dark">
                                        <p className="text-sm text-stone-700 uppercase tracking-wider">Vacancy Rate</p>
                                        <p className="text-sm font-semibold text-ivory mt-1">{vacancyRate.toFixed(1)}%</p>
                                    </div>
                                )}
                                {rentGrowth != null && (
                                    <div className="p-3 bg-graphite rounded border border-slate-dark">
                                        <p className="text-sm text-stone-700 uppercase tracking-wider">Rent Growth</p>
                                        <p className="text-sm font-semibold text-ivory mt-1">
                                            {rentGrowth < 1
                                                ? (rentGrowth * 100).toFixed(1)
                                                : rentGrowth.toFixed(1)}% /yr
                                        </p>
                                    </div>
                                )}
                                <div className="p-3 bg-graphite rounded border border-slate-dark">
                                    <p className="text-sm text-stone-700 uppercase tracking-wider">Property Tax</p>
                                    <p className="text-sm font-semibold text-ivory mt-1">
                                        {market.market_id === 'norfolk' ? '1.21%' : '--'}
                                    </p>
                                </div>
                                {rentSource && (
                                    <div className="p-3 bg-graphite rounded border border-slate-dark col-span-2 sm:col-span-1">
                                        <p className="text-sm text-stone-700 uppercase tracking-wider">Rent Source</p>
                                        <p className="text-sm font-semibold text-purple-300 mt-1 truncate" title={rentSource}>
                                            {rentSource}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Phase 153: Census demographic panel for non-Philly markets */}
                {data.census_summary && (
                    <CensusDemographicPanel
                        census={data.census_summary}
                        marketName={market.display_name}
                    />
                )}

                {/* Non-Zandi sections that work for any market */}
                <InvestmentIntelligenceCard data={data} />
                <DemographicInsightsCard data={data} />
            </div>
        );
    }

    // Philadelphia empty state: show generic "not yet" message with backtesting info
    if (!scoring && !zandi) {
        return (
            <div className="space-y-6 bg-obsidian p-6 rounded-lg">
                <div className="p-6 bg-charcoal rounded-lg border border-slate-dark text-center">
                    <h3 className="text-lg font-serif italic text-gold mb-2">Scoring Data Not Available</h3>
                    <p className="text-sm text-stone-700 mb-4">
                        This property doesn't have Zandi submarket scoring or deal quality data yet.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="p-4 bg-graphite rounded border border-slate-dark">
                            <p className="text-sm text-purple-400 font-medium mb-1 uppercase tracking-wider">ZANDI MARKET SCORE</p>
                            <p className="text-2xl font-bold font-mono text-stone-700">--</p>
                            <p className="text-sm text-stone-700 mt-1">Submarket analysis</p>
                        </div>
                        <div className="p-4 bg-graphite rounded border border-slate-dark">
                            <p className="text-sm text-emerald font-medium mb-1 uppercase tracking-wider">PROPERTY SCORE</p>
                            <p className="text-2xl font-bold font-mono text-stone-700">--</p>
                            <p className="text-sm text-stone-700 mt-1">Deal quality grade</p>
                        </div>
                    </div>
                </div>

                {/* Backtesting info always shows */}
                <div className="p-4 bg-charcoal rounded-lg border border-purple-500/30">
                    <div className="flex items-start gap-4">
                        <TrendingUp className="w-6 h-6 text-purple-400 mt-1" />
                        <div>
                            <h4 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">Backtesting Validation (2018-2023)</h4>
                            <div className="mt-2 text-sm text-stone-700">
                                Validated against 174 submarket-years. Top predictor of 5-year returns.
                            </div>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-graphite p-3 rounded border border-slate-dark">
                                    <div className="text-sm text-stone-700">Top Predictor</div>
                                    <div className="font-bold text-purple-300">Momentum</div>
                                    <div className="text-sm text-emerald">+0.42 correlation</div>
                                </div>
                                <div className="bg-graphite p-3 rounded border border-slate-dark">
                                    <div className="text-sm text-stone-700">2nd Predictor</div>
                                    <div className="font-bold text-purple-300">Rent Gap</div>
                                    <div className="text-sm text-emerald">+0.38 correlation</div>
                                </div>
                                <div className="bg-graphite p-3 rounded border border-slate-dark">
                                    <div className="text-sm text-stone-700">Validation</div>
                                    <div className="font-bold text-purple-300">Top Quartile</div>
                                    <div className="text-sm text-emerald">Avg 18.2% Return</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 bg-obsidian min-h-full">
            {/* SECTION A: ZANDI SCORING OVERVIEW */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Market Score - only show if zandi data exists */}
                {zandi && (
                    <div className="bg-charcoal border-l-4 border-purple-500 p-5 rounded-r-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-serif italic text-gold">Zandi Market Score</h3>
                                <p className="mt-1 text-sm text-stone-700">{zandi.name}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-4xl font-bold font-mono text-purple-400">{formatScore(zandi.composite_score)}</p>
                                {zandi.rank && (
                                    <span className="inline-block mt-1 px-2 py-0.5 text-sm font-semibold uppercase tracking-wider bg-purple-500/20 text-purple-300 rounded">
                                        Rank #{zandi.rank}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-4">
                            <div className="p-3 bg-graphite rounded border border-slate-dark text-center">
                                <p className="text-sm text-stone-700 uppercase tracking-wider">Momentum</p>
                                <p className="text-lg font-mono font-semibold text-ivory">{formatScore(zandi.momentum_score)}<span className="text-stone-700">/100</span></p>
                            </div>
                            <div className="p-3 bg-graphite rounded border border-slate-dark text-center">
                                <p className="text-sm text-stone-700 uppercase tracking-wider">Rent Gap</p>
                                <p className="text-lg font-mono font-semibold text-ivory">{formatScore(zandi.rent_gap_score)}<span className="text-stone-700">/100</span></p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Property Score - only show if scoring data exists */}
                {scoring && (
                    <div className={`bg-charcoal border-l-4 p-5 rounded-r-lg ${scoring.grade === 'A' ? 'border-emerald' : 'border-gold'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-serif italic text-gold">Property Score</h3>
                                <p className="mt-1 text-sm text-stone-700">Deal Quality</p>
                            </div>
                            <div className="text-right">
                                <p className={`text-4xl font-bold font-mono ${scoring.grade === 'A' ? 'text-emerald' : 'text-gold'}`}>
                                    {scoring.total_score}
                                </p>
                                <span className={`inline-block mt-1 px-2 py-0.5 text-sm font-semibold uppercase tracking-wider rounded ${
                                    scoring.grade === 'A' ? 'bg-emerald/20 text-emerald' : 'bg-gold/20 text-gold'
                                }`}>
                                    Grade {scoring.grade}
                                </span>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-stone-700">Driven by:</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {scoring.rationale?.slice(0, 3).map((r: string, i: number) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-graphite border border-slate-dark rounded text-stone-700">
                                        <CheckCircle className="w-3 h-3 text-emerald" />
                                        {r.split(':')[0]}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* SECTION B: ZANDI METHODOLOGY DEEP DIVE */}
            {zandi?.raw_data && (
                <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
                    <SectionTitle>Zandi Methodology Breakdown</SectionTitle>
                    <p className="text-sm text-stone-700 -mt-4 mb-6">
                        Transparent scoring formulas based on local market data.
                    </p>

                    <div className="space-y-4">
                        {/* Rent Gap */}
                        <div className="border-l-4 border-emerald pl-4 py-2 bg-emerald/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">Rent Gap Score: <span className="text-emerald">{formatScore(zandi.rent_gap_score)}</span>/100</p>
                                <GoldBadge variant="ssot">Weight: 25%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Formula: 50 + (({formatCurrency(zandi.raw_data.metro_egi)} - {formatCurrency(zandi.raw_data.egi_per_unit)}) / {formatCurrency(zandi.raw_data.metro_egi)}) × 100
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-sm text-stone-700">
                                <div>Submarket EGI: <span className="font-semibold text-ivory">{formatCurrency(zandi.raw_data.egi_per_unit)}</span></div>
                                <div>Metro EGI: <span className="font-semibold text-ivory">{formatCurrency(zandi.raw_data.metro_egi)}</span></div>
                                <div className="text-emerald">
                                    Gap: {zandi.raw_data.metro_egi && zandi.raw_data.egi_per_unit
                                        ? `${((zandi.raw_data.metro_egi - zandi.raw_data.egi_per_unit) / zandi.raw_data.metro_egi * 100).toFixed(1)}%`
                                        : '--'} (Growth Potential)
                                </div>
                            </div>
                        </div>

                        {/* MOBI (Affordability) */}
                        <div className="border-l-4 border-purple-500 pl-4 py-2 bg-purple-500/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">MOBI Score: <span className="text-purple-400">{formatScore(zandi.mobi_score)}</span>/100</p>
                                <GoldBadge variant="gold">Weight: 15%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Formula: 70 - ((MOBI - 1.0) x 100) | Lower ownership costs relative to rents = stronger investment market
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-sm text-stone-700">
                                <div>MOBI Ratio: <span className="font-semibold text-ivory">
                                    {zandi.raw_data.mobi_ratio?.toFixed(3) ?? '--'}
                                </span></div>
                                <div>Interpretation: <span className="font-semibold text-ivory">
                                    {zandi.raw_data.mobi_ratio != null
                                        ? zandi.raw_data.mobi_ratio < 1.0 ? 'Affordable (Investment Friendly)' : zandi.raw_data.mobi_ratio < 1.2 ? 'Moderate' : 'Expensive (Limited Upside)'
                                        : '--'}
                                </span></div>
                                <div className="text-purple-300">
                                    Source: Census ACS
                                </div>
                            </div>
                        </div>

                        {/* Momentum */}
                        <div className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-500/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">Momentum Score: <span className="text-blue-400">{formatScore(zandi.momentum_score)}</span>/100</p>
                                <GoldBadge variant="gold">Weight: 15%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Phase = f(cagr vs metro_cagr, yoy vs cagr) | Score thresholds: Entry=85, Accelerating=70, Mature=50, Declining=30
                            </p>
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-stone-700">
                                <div>5yr CAGR: <span className="font-semibold text-ivory">{formatPercent(zandi.raw_data.cagr_5yr)}</span></div>
                                <div>YoY Change: <span className="font-semibold text-ivory">{formatPercent(zandi.raw_data.yoy_change)}</span></div>
                                <div>Metro CAGR: <span className="font-semibold text-ivory">{formatPercent(zandi.raw_data.metro_cagr)}</span></div>
                                <div className="text-blue-300">
                                    Phase: {getMomentumPhase(zandi.raw_data.cagr_5yr, zandi.raw_data.yoy_change, zandi.raw_data.metro_cagr)}
                                </div>
                            </div>
                            {(zandi.raw_data.yoy_change == null || zandi.raw_data.metro_cagr == null) && zandi.raw_data.cagr_5yr != null && (
                                <p className="mt-1 text-sm text-stone-700 italic">
                                    Score based on 5yr CAGR only — YoY and metro comparison require additional value index history
                                </p>
                            )}
                        </div>

                        {/* Vacancy */}
                        <div className="border-l-4 border-cyan-500 pl-4 py-2 bg-cyan-500/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">Vacancy Score: <span className="text-cyan-400">{formatScore(zandi.vacancy_score)}</span>/100</p>
                                <GoldBadge variant="gold">Weight: 15%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Formula: 100 - (vacancy_rate x 100)
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-sm text-stone-700">
                                <div>Vacancy Rate: <span className="font-semibold text-ivory">
                                    {zandi.raw_data.vacancy_rate != null
                                        ? `${(zandi.raw_data.vacancy_rate * 100).toFixed(1)}%`
                                        : '--'}
                                </span></div>
                                <div>Market Tightness: <span className="font-semibold text-ivory">
                                    {zandi.raw_data.vacancy_rate != null
                                        ? zandi.raw_data.vacancy_rate < 0.05 ? 'Tight' : zandi.raw_data.vacancy_rate < 0.08 ? 'Healthy' : 'Soft'
                                        : '--'}
                                </span></div>
                                <div className="text-cyan-300">
                                    Source: Census ACS
                                </div>
                            </div>
                        </div>

                        {/* Valuation */}
                        <div className="border-l-4 border-amber-500 pl-4 py-2 bg-amber-500/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">Valuation Score: <span className="text-amber-400">{formatScore(zandi.valuation_score)}</span>/100</p>
                                <GoldBadge variant="gold">Weight: 10%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Formula: 50 + (Value CAGR - EGI CAGR) × 1000
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-sm text-stone-700">
                                <div>Value CAGR: <span className="font-semibold text-ivory">{formatPercent(zandi.raw_data.cagr_5yr)}</span></div>
                                <div>EGI CAGR: <span className="font-semibold text-ivory">{formatPercent(zandi.raw_data.egi_cagr_5yr)}</span></div>
                                <div className={zandi.raw_data.price_rent_spread && zandi.raw_data.price_rent_spread > 0 ? 'text-emerald' : 'text-amber-400'}>
                                    Spread: {zandi.raw_data.price_rent_spread != null
                                        ? `${(zandi.raw_data.price_rent_spread * 100).toFixed(1)}%`
                                        : '--'} (Price vs Rent)
                                </div>
                            </div>
                        </div>

                        {/* Permits */}
                        <div className="border-l-4 border-orange-500 pl-4 py-2 bg-orange-500/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">Permit Score: <span className="text-orange-400">{formatScore(zandi.permit_score)}</span>/100</p>
                                <GoldBadge variant="gold">Weight: 10%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Thresholds: &gt;100 for-rent = 30 | 50-100 = 50 | 20-50 = 70 | &lt;20 = 90
                            </p>
                            {zandi.raw_data.permit_count_for_rent != null ? (
                                <div className="mt-2 grid grid-cols-3 gap-2 text-sm text-stone-700">
                                    <div>For-Rent Permits: <span className="font-semibold text-ivory">
                                        {zandi.raw_data.permit_count_for_rent.toLocaleString()}
                                    </span></div>
                                    <div>Density: <span className="font-semibold text-ivory">
                                        {zandi.raw_data.permit_density != null
                                            ? `${zandi.raw_data.permit_density.toFixed(0)} /sq mi/yr`
                                            : '--'}
                                    </span></div>
                                    <div className="text-orange-300">
                                        Source: {market.permit_source_name} CARTO
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-2 text-sm text-stone-700 italic">
                                    Permit data covers {market.display_name} city limits only — suburban submarkets use neutral score (50)
                                </div>
                            )}
                        </div>

                        {/* Spillover */}
                        <div className="border-l-4 border-indigo-500 pl-4 py-2 bg-indigo-500/5 rounded-r">
                            <div className="flex justify-between items-center">
                                <p className="font-bold text-ivory">Spillover Score: <span className="text-indigo-400">{formatScore(zandi.spillover_score)}</span>/100</p>
                                <GoldBadge variant="gold">Weight: 10%</GoldBadge>
                            </div>
                            <p className="text-sm font-mono bg-graphite p-2 rounded mt-2 border border-slate-dark text-stone-700">
                                Formula: avg(adjacent_submarket_momentum_scores)
                            </p>
                            {zandi.raw_data.adjacent_submarkets && zandi.raw_data.adjacent_submarkets.length > 0 ? (
                                <div className="mt-2 space-y-1">
                                    <p className="text-sm text-stone-700 uppercase tracking-wider">
                                        Adjacent Submarkets ({zandi.raw_data.adjacent_count ?? zandi.raw_data.adjacent_submarkets.length})
                                    </p>
                                    <div className="grid grid-cols-2 gap-1 text-sm text-stone-700">
                                        {zandi.raw_data.adjacent_submarkets.map((adj, i) => (
                                            <div key={i} className="flex justify-between px-2 py-1 bg-graphite/50 rounded">
                                                <span className="truncate">{adj.name}</span>
                                                <span className={`font-mono font-semibold ${
                                                    adj.momentum_score >= 75 ? 'text-emerald' :
                                                    adj.momentum_score >= 50 ? 'text-amber-400' : 'text-red-400'
                                                }`}>
                                                    {adj.momentum_score.toFixed(0)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-2 text-sm text-stone-700 italic">
                                    No adjacent submarket data available for this location — using neutral score (50)
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* SECTION B.1: HISTORICAL EGI & RENT GAP VISUALIZATION (Phase 43 / Phase 124) */}
            {zandi?.raw_data && (
                <>
                    {/* Historical EGI Chart - prefer API time-series data, fall back to legacy/synthetic */}
                    {(() => {
                        // Priority 1: Real API time-series data (16-year monthly)
                        if (egiHistory.data?.points && egiHistory.data.points.length > 0) {
                            return (
                                <ZandiHistoricalChart
                                    data={egiHistory.data.points}
                                    submarketName={zandi.name}
                                />
                            );
                        }

                        // Show loading skeleton while API data is being fetched
                        if (egiHistory.isLoading) {
                            return <ZandiHistoricalChartSkeleton />;
                        }

                        // Priority 2: Legacy egi_history from zandi raw_data
                        const hasHistory = zandi.raw_data.egi_history && zandi.raw_data.egi_history.length > 0;
                        if (hasHistory) {
                            return (
                                <ZandiHistoricalChart
                                    history={zandi.raw_data.egi_history!}
                                    submarketName={zandi.name}
                                />
                            );
                        }

                        // Priority 3: Generate synthetic 16-year history from current EGI + CAGR
                        const currentEgi = zandi.raw_data.egi_per_unit || zandi.raw_data.submarket_egi;
                        const cagr = zandi.raw_data.egi_cagr_5yr ?? zandi.raw_data.cagr_5yr;
                        if (currentEgi != null && currentEgi > 0 && cagr != null) {
                            return (
                                <ZandiHistoricalChart
                                    history={generateSyntheticHistory(currentEgi, cagr)}
                                    submarketName={zandi.name}
                                />
                            );
                        }

                        return null;
                    })()}

                    {/* Rent Gap Visualization - show if both submarket and metro EGI available */}
                    {(zandi.raw_data.egi_per_unit || zandi.raw_data.submarket_egi) && zandi.raw_data.metro_egi && (
                        <RentGapVisualization
                            submarketEgi={zandi.raw_data.egi_per_unit || zandi.raw_data.submarket_egi || 0}
                            metroEgi={zandi.raw_data.metro_egi}
                            submarketName={zandi.name}
                        />
                    )}
                </>
            )}

            {/* SECTION C: COMPOSITE SCORE BREAKDOWN */}
            <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <SectionTitle>Composite Score Breakdown</SectionTitle>
                        <p className="text-sm text-stone-700 -mt-4">
                            7 factors weighted by backtest correlation with 5-year returns
                        </p>
                    </div>
                    <GoldBadge variant="gold">174 submarket-years tested</GoldBadge>
                </div>

                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30 mb-6">
                    <p className="text-sm text-stone-700">
                        <strong className="text-purple-300">Backtest Results:</strong> Composite score has +0.44 correlation with 5-year forward returns.
                        Top quartile submarkets averaged 18.2% annualized returns vs 8.4% for bottom quartile.
                    </p>
                </div>

                {/* All 7 Scoring Factors */}
                {(() => {
                    const rd = zandi?.raw_data;
                    const defaultedRentGap = isScoreDefaulted(zandi?.rent_gap_score, rd?.egi_per_unit != null);
                    const defaultedMobi = isScoreDefaulted(zandi?.mobi_score, rd?.mobi_ratio != null);
                    const defaultedMomentum = isScoreDefaulted(zandi?.momentum_score, rd?.cagr_5yr != null || rd?.yoy_change != null);
                    const defaultedVacancy = isScoreDefaulted(zandi?.vacancy_score, rd?.vacancy_rate != null);
                    const defaultedValuation = isScoreDefaulted(zandi?.valuation_score, rd?.price_rent_spread != null);
                    const defaultedPermits = isScoreDefaulted(zandi?.permit_score, rd?.permit_count_for_rent != null || rd?.permit_density != null);
                    const defaultedSpillover = isScoreDefaulted(zandi?.spillover_score, rd?.adjacent_submarkets != null && rd.adjacent_submarkets.length > 0);
                    const hasAnyDefaulted = defaultedRentGap || defaultedMobi || defaultedMomentum || defaultedVacancy || defaultedValuation || defaultedPermits || defaultedSpillover;

                    return (
                        <>
                            <div className="space-y-2">
                                <ScoreRow
                                    weight="25%"
                                    label="Rent Gap"
                                    correlation="r = +0.33"
                                    correlationPositive={true}
                                    description="Lower rents vs metro = room to grow"
                                    score={zandi?.rent_gap_score}
                                    colorClass="emerald"
                                    isActive={zandi?.rent_gap_score != null}
                                    defaulted={defaultedRentGap}
                                />
                                <ScoreRow
                                    weight="15%"
                                    label="MOBI (Affordability)"
                                    correlation="r = +0.31"
                                    correlationPositive={true}
                                    description="Low barriers attract institutional capital"
                                    score={zandi?.mobi_score}
                                    colorClass="purple-400"
                                    isActive={zandi?.mobi_score != null}
                                    defaulted={defaultedMobi}
                                />
                                <ScoreRow
                                    weight="15%"
                                    label="Momentum"
                                    correlation="r = +0.15"
                                    correlationPositive={true}
                                    description="CAGR trend and market cycle phase"
                                    score={zandi?.momentum_score}
                                    colorClass="blue-400"
                                    isActive={zandi?.momentum_score != null}
                                    defaulted={defaultedMomentum}
                                />
                                <ScoreRow
                                    weight="15%"
                                    label="Vacancy"
                                    correlation="r = -0.17"
                                    correlationPositive={false}
                                    description="Low vacancy = strong demand"
                                    score={zandi?.vacancy_score}
                                    colorClass="cyan-400"
                                    isActive={zandi?.vacancy_score != null}
                                    defaulted={defaultedVacancy}
                                />
                                <ScoreRow
                                    weight="10%"
                                    label="Valuation"
                                    correlation="r = +0.35"
                                    correlationPositive={true}
                                    description="Price momentum vs rent growth"
                                    score={zandi?.valuation_score}
                                    colorClass="amber-400"
                                    isActive={zandi?.valuation_score != null}
                                    defaulted={defaultedValuation}
                                />
                                <ScoreRow
                                    weight="10%"
                                    label="Permits"
                                    correlation="activity"
                                    correlationPositive={true}
                                    description={zandi?.raw_data?.permit_density != null
                                        ? `${zandi.raw_data.permit_density.toFixed(0)} permits/sq mi/yr`
                                        : 'Development activity indicator'
                                    }
                                    score={zandi?.permit_score}
                                    colorClass="orange-400"
                                    isActive={zandi?.permit_score != null}
                                    defaulted={defaultedPermits}
                                />
                                <ScoreRow
                                    weight="10%"
                                    label="Spillover"
                                    correlation="adjacency"
                                    correlationPositive={true}
                                    description={zandi?.raw_data?.adjacent_count != null
                                        ? `Avg of ${zandi.raw_data.adjacent_count} adjacent submarket momentum scores`
                                        : 'Adjacent submarket momentum'
                                    }
                                    score={zandi?.spillover_score}
                                    colorClass="indigo-400"
                                    isActive={zandi?.spillover_score != null}
                                    defaulted={defaultedSpillover}
                                />
                            </div>
                            {hasAnyDefaulted && (
                                <p className="text-sm text-stone-700 mt-3 italic">
                                    * Neutral score (50) — source data not available for this submarket
                                </p>
                            )}
                        </>
                    );
                })()}

                {/* Composite Total Score - FOCAL POINT */}
                {zandi?.composite_score != null && (
                    <div className="mt-8 pt-8 border-t-2 border-purple-500/50">
                        <div className="p-8 bg-gradient-to-br from-purple-600/30 via-purple-500/20 to-purple-900/30 rounded-xl border-2 border-purple-500/50 shadow-lg shadow-purple-500/10">
                            {/* Center-aligned layout */}
                            <div className="flex flex-col items-center text-center">
                                {/* Label and Badge */}
                                <div className="flex items-center gap-3 mb-4">
                                    <p className="text-sm font-semibold text-purple-300 uppercase tracking-[0.2em]">
                                        Composite Score
                                    </p>
                                    <GoldBadge variant={getScoreInterpretation(zandi.composite_score).badgeVariant}>
                                        {getScoreInterpretation(zandi.composite_score).label}
                                    </GoldBadge>
                                </div>

                                {/* Large Score Display - 72px */}
                                <div className="relative">
                                    <p className="text-[72px] leading-none font-bold font-mono text-transparent bg-clip-text bg-gradient-to-b from-purple-300 to-purple-500">
                                        {formatScore(zandi.composite_score)}
                                    </p>
                                    <p className="text-lg text-purple-400/70 mt-2">
                                        / 100
                                    </p>
                                </div>

                                {/* Description */}
                                <p className="text-sm text-purple-300/80 mt-4 max-w-md">
                                    {getScoreInterpretation(zandi.composite_score).description}
                                </p>

                                {/* Rank if available */}
                                {zandi.rank && (
                                    <div className="mt-4 px-4 py-2 bg-purple-500/10 rounded-full border border-purple-500/30">
                                        <span className="text-sm text-purple-300">
                                            Ranked <span className="font-bold text-gold">#{zandi.rank}</span> of {market.submarket_count} {market.display_name} submarkets
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Legend */}
                <div className="mt-4 pt-3 border-t border-slate-dark text-sm text-stone-700">
                    All scores computed from Zandi/Moody's, Census ACS, {market.permit_source_name} CARTO API, and adjacent submarket momentum.
                </div>
            </div>

            {/* SECTION C.4: ZANDI DEAL IMPACT NARRATIVE (Phase 65) */}
            <ZandiDealImpact data={data} />

            {/* SECTION C.5: METRO MARKET CONTEXT (Yardi Matrix) */}
            {yardiData && (
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <h3 className="text-lg text-slate-100">{yardiData.metro_name}</h3>
                            <span className="text-sm text-amber-400 font-medium uppercase tracking-wider">Yardi Matrix</span>
                        </div>
                    </div>

                    {/* 3-Metric Row */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        {/* YoY Rent Growth */}
                        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                            <p className="text-sm text-slate-400 mb-1">YoY Rent Growth</p>
                            <p className={`text-xl font-bold ${
                                yardiData.rent_growth_comparison.yardi_value >= 0 ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                                {formatPercentFromDecimal(yardiData.rent_growth_comparison.yardi_value)}
                            </p>
                            <p className="text-sm text-slate-500">Current</p>
                        </div>

                        {/* 2026 Forecast */}
                        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                            <p className="text-sm text-slate-400 mb-1">2026 Forecast</p>
                            <p className="text-xl font-bold text-amber-400">
                                {formatPercentFromDecimal(yardiData.market_context.forecast_2026)}
                            </p>
                            <p className="text-sm text-slate-500">{getForecastAssessment(yardiData)}</p>
                        </div>

                        {/* Supply (Completions) */}
                        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                            <p className="text-sm text-slate-400 mb-1">Supply (Completions)</p>
                            <p className={`text-xl font-bold ${
                                (yardiData.market_context.completions_pct || 0) > 0.03 ? 'text-red-400' : 'text-slate-300'
                            }`}>
                                {formatPercentFromDecimal(yardiData.market_context.completions_pct)}
                            </p>
                            <p className="text-sm text-slate-500">{getSupplyAssessment(yardiData.market_context.completions_pct)}</p>
                        </div>
                    </div>

                    {/* SSOT vs Yardi Comparison Box */}
                    <div className="bg-slate-900/50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-slate-100 mb-3">SSOT vs Yardi Comparison</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-slate-400">Your Rent Growth Assumption:</span>
                                <span className="text-slate-100 font-mono">
                                    {formatPercentFromDecimal(yardiData.rent_growth_comparison.ssot_value)}
                                    <span className="text-slate-500 ml-1">({yardiData.tier})</span>
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-400">Yardi Market Benchmark:</span>
                                <span className="text-slate-100 font-mono">
                                    {formatPercentFromDecimal(yardiData.rent_growth_comparison.yardi_value)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                                <span className="text-slate-400">Variance:</span>
                                <div className="flex items-center gap-2">
                                    <span className={`font-mono font-semibold ${
                                        yardiData.rent_growth_comparison.variance > 0 ? 'text-red-400' : 'text-emerald-400'
                                    }`}>
                                        {yardiData.rent_growth_comparison.variance > 0 ? '+' : ''}
                                        {(yardiData.rent_growth_comparison.variance * 100).toFixed(0)}bp
                                    </span>
                                    <AssessmentBadge assessment={yardiData.rent_growth_comparison.assessment} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Source Footer */}
                    <div className="mt-4 text-sm text-slate-500">
                        Source: {yardiData.source}
                    </div>
                </div>
            )}

            {/* Loading state for Yardi data */}
            {yardiLoading && (
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center justify-center py-4 text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Loading metro market context...
                    </div>
                </div>
            )}

            {/* SECTION C.6: ZANDI × YARDI CROSS-VALIDATION (Phase 66) */}
            {zandi && yardiData && (
                <ZandiYardiCrossValidation zandi={zandi} yardiData={yardiData} />
            )}

            {/* SECTION E: INVESTMENT INTELLIGENCE (Phase 74) */}
            <InvestmentIntelligenceCard data={data} />
            <DemographicInsightsCard data={data} />

            {/* SECTION F: RENT GROWTH INTELLIGENCE (Phase 116) */}
            {submarketName && <RentGrowthCard submarketName={submarketName} marketId={market.market_id} />}

            {/* SECTION D: SUBMARKET COMPARISON TABLE */}
            {submarketName && (
                isLoadingComparison ? (
                    <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
                        <div className="flex items-center justify-center py-8 text-stone-700">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Loading submarket comparison...
                        </div>
                    </div>
                ) : submarketComparison && submarketComparison.length > 0 ? (
                    <SubmarketComparisonTable
                        currentSubmarket={submarketName}
                        submarkets={submarketComparison}
                    />
                ) : null
            )}
        </div>
    );
};
